import React, { useState, useEffect } from 'react'
import {
  Form,
  Button,
  Badge,
  Dropdown,
  ListGroup,
  Modal,
} from 'react-bootstrap'
import { Send, X, MoreHorizontal, Type, Smile, Paperclip } from 'lucide-react'
import 'bootstrap/dist/css/bootstrap.min.css'
import Header from '../Components/Header'
import moment from 'moment'
import EmojiPicker from 'emoji-picker-react'
import axios from 'axios'

export default function LMSMessages() {
  const [recipientInput, setRecipientInput] = useState('')
  const [recipientList, setRecipientList] = useState([])
  const [filteredRecipients, setFilteredRecipients] = useState([])
  const [selectedRecipients, setSelectedRecipients] = useState([])
  const [recentChats, setRecentChats] = useState([])
  const [messages, setMessages] = useState([])
  const [messageInput, setMessageInput] = useState('')
  const [showAttachments, setShowAttachments] = useState(false)
  const [fileInput, setFileInput] = useState(null)
  const [showEmojiPicker, setShowEmojiPicker] = useState(false)
  const [user, setUser] = useState(null)
  const [isSending, setIsSending] = useState(false)
  const [selectedChat, setSelectedChat] = useState(null)
  const token = localStorage.getItem('jwttoken')

  useEffect(() => {
    const fetchMessages = async () => {
      try {
        const response = await axios.get(
          'https://kaasbackend.vetrinahealthcare.com/socialapp/messages/',
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          }
        )

        const { user, messages } = response.data
        setUser(user)

        const updatedMessages = messages.map((msg) => ({
          id: msg.id,
          sender: msg.sender,
          receiver: msg.receiver,
          content: msg.content,
          timestamp: msg.timestamp,
          is_read: msg.is_read,
          forwarded_from: msg.forwarded_from_info || null,
          attachment: msg.attachment,
          attachment_type: msg.attachment_type,
          attachment_preview: msg.attachment_preview,
          is_deleted: msg.is_deleted,
        }))

        setMessages(updatedMessages)

        // Extract recent chats
        const chatsMap = new Map()
        updatedMessages.forEach((msg) => {
          const chatKey =
            msg.sender === `${user.first_name} ${user.last_name}`
              ? msg.receiver
              : msg.sender
          if (!chatsMap.has(chatKey)) {
            chatsMap.set(chatKey, {
              name: chatKey,
              message: msg.content,
              time: msg.timestamp,
            })
          }
        })

        setRecentChats(Array.from(chatsMap.values()))
      } catch (error) {
        console.error('Error fetching messages:', error)
      }
    }

    fetchMessages()
  }, [token])

  const handleChatClick = (chat) => {
    setSelectedChat(chat.user) // Stores the selected chat ID

    // Update selected recipients
    setSelectedRecipients([
      {
        user_id: chat.user,
        first_name: chat.first_name,
        last_name: chat.last_name,
      },
    ])
  }

  // useEffect(() => {
  //   const interval = setInterval(() => {
  //     fetchMessages();
  //   }, 3000); // Fetch every 3 seconds

  //   return () => clearInterval(interval);
  // }, []);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await axios.get(
          'https://kaasbackend.vetrinahealthcare.com/socialapp/users/',
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          }
        )
        setRecipientList(response.data) // Store fetched users in recipientList
      } catch (error) {
        console.error('Error fetching users:', error)
      }
    }

    fetchUsers()
  }, [token])

  const handleRecipientInput = (e) => {
    const input = e.target.value
    setRecipientInput(input)

    if (input) {
      const filtered = recipientList.filter(
        (user) =>
          user.first_name.toLowerCase().includes(input.toLowerCase()) &&
          !selectedRecipients.some((rec) => rec.user_id === user.user_id)
      )
      setFilteredRecipients(filtered)
    } else {
      setFilteredRecipients([])
    }
  }

  const addRecipient = (user) => {
    if (!selectedRecipients.some((rec) => rec.user_id === user.user_id)) {
      setSelectedRecipients((prev) => [...prev, user])
    }
    setRecipientInput('')
    setFilteredRecipients([])
  }

  const removeRecipient = (userId) => {
    setSelectedRecipients((prev) =>
      prev.filter((recipient) => recipient.user_id !== userId)
    )
  }

  const handleSendMessage = async () => {
    if (!messageInput.trim() || selectedRecipients.length === 0) {
      return // Do not send if no message or recipient is selected
    }

    const newMessage = {
      receiver_id:
        selectedRecipients.length === 1
          ? selectedRecipients[0].user_id
          : selectedRecipients.map((recipient) => recipient.user_id),
      content: messageInput.trim(),
    }

    try {
      const response = await axios.post(
        'https://kaasbackend.vetrinahealthcare.com/socialapp/messages/send/',
        newMessage,
        { headers: { Authorization: `Bearer ${token}` } }
      )

      console.log('Message sent:', response.data)

      // Add the sent message to the state immediately
      setMessages((prevMessages) => [
        ...prevMessages,
        {
          id: response.data.id, // Use the actual message ID from response
          sender: user?.first_name + ' ' + user?.last_name,
          receiver: selectedRecipients
            .map((r) => r.first_name + ' ' + r.last_name)
            .join(', '),
          content: newMessage.content,
          timestamp: new Date().toISOString(),
          is_read: false,
          is_deleted: false,
        },
      ])

      setMessageInput('') // Clear the input field after sending
    } catch (error) {
      console.error('Error sending message:', error.response?.data || error)
    }
  }

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSendMessage()
    }
  }

  const handleFileInput = (e) => {
    setFileInput(e.target.files[0])
  }

  const toggleAttachments = () => setShowAttachments((prev) => !prev)

  const toggleEmojiPicker = () => setShowEmojiPicker((prev) => !prev)

  const addEmoji = (event) => {
    console.log('Event:', event) // Inspect the event for debugging
    const emoji = event.emoji // Extract the emoji from the event
    if (emoji) {
      setMessageInput((prev) => prev + emoji) // Append emoji to the input
      setShowEmojiPicker(false) // Close the emoji picker
    } else {
      console.error('Emoji not found in event')
    }
  }

  const formatTime = (timestamp) => {
    return moment(timestamp).fromNow()
  }

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        showEmojiPicker &&
        !event.target.closest('.emoji-picker-container') && // Ensure you're not clicking inside the picker
        !event.target.closest('.emoji-button') // Ensure you're not clicking the toggle button
      ) {
        setShowEmojiPicker(false)
      }
    }

    document.addEventListener('click', handleClickOutside)

    return () => {
      document.removeEventListener('click', handleClickOutside)
    }
  }, [showEmojiPicker])

  const markMessageAsRead = async (message_id) => {
    try {
      await axios.post(
        `https://kaasbackend.vetrinahealthcare.com/socialapp/messages/${message_id}/read/`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      )

      setMessages((prevMessages) =>
        prevMessages.map((msg) =>
          msg.id === message_id ? { ...msg, is_read: true } : msg
        )
      )
    } catch (error) {
      console.error('Error marking message as read:', error)
    }
  }

  const filteredMessages = messages.filter(
    (msg) =>
      (msg.sender === selectedChat || msg.receiver === selectedChat) &&
      !msg.is_deleted
  )

  return (
    <>
      <Header />
      <div className='d-flex vh-90'>
        {/* Sidebar */}
        <div
          className='border-end'
          style={{ width: '300px', backgroundColor: '#f8f9fa' }}
        >
          {/* Sidebar Header */}
          <div className='p-3 border-bottom d-flex justify-content-between align-items-center'>
            <h1 className='h4 mb-0' style={{ fontFamily: 'Arial, sans-serif' }}>
              Messages
            </h1>
            <Dropdown align='end'>
              <Dropdown.Toggle
                variant='link'
                className='p-0 border-0 text-dark'
              >
                <MoreHorizontal className='h-5 w-5' />
              </Dropdown.Toggle>
              <Dropdown.Menu>
                <Dropdown.Item>
                  <span className='me-2'>📦</span> Archived Messages
                </Dropdown.Item>
                <Dropdown.Item>
                  <span className='me-2'>🔔</span> Notification Preferences
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          </div>

          {/* Recent Chats */}
          <div className='p-3'>
            <h5 className='text-muted mb-3'>Recent Chats</h5>
            <ListGroup style={{ maxHeight: '400px', overflowY: 'auto' }}>
              {recentChats.map((chat, index) => (
                <ListGroup.Item
                  key={index}
                  className={`d-flex align-items-center gap-2 ${
                    selectedChat === chat.user ? 'bg-light' : ''
                  }`}
                  onClick={() => handleChatClick(chat)}
                  style={{ cursor: 'pointer' }}
                >
                  <div
                    className='rounded-circle bg-primary text-white d-flex justify-content-center align-items-center'
                    style={{ width: '40px', height: '40px' }}
                  >
                    {chat.name[0]}
                  </div>
                  <div className='flex-grow-1'>
                    <div className='fw-bold'>{chat.name}</div>
                    <small className='text-muted'>{chat.message}</small>
                  </div>
                  <small className='text-muted'>{formatTime(chat.time)}</small>
                </ListGroup.Item>
              ))}
            </ListGroup>
          </div>
        </div>

        {/* Main Content */}
        <div className='flex-grow-1 d-flex flex-column'>
          {/* Recipients Section */}
          <div className='border-bottom p-3 bg-light'>
            <div className='d-flex align-items-center gap-2'>
              <span className='text-muted'>
                {selectedRecipients.length > 0
                  ? selectedRecipients
                      .map((r) => `${r.first_name} ${r.last_name}`)
                      .join(', ')
                  : 'To:'}
              </span>
              <div className='position-relative w-100'>
                <div className='d-flex flex-wrap align-items-center gap-2'>
                  {selectedRecipients.map((recipient) => (
                    <Badge
                      key={recipient.user_id}
                      className='d-flex align-items-center gap-1 px-2 py-1'
                      bg='primary'
                      text='light'
                    >
                      {recipient.first_name} {recipient.last_name}
                      <X
                        size={14}
                        onClick={() => removeRecipient(recipient.user_id)}
                        style={{ cursor: 'pointer' }}
                      />
                    </Badge>
                  ))}
                  <Form.Control
                    type='text'
                    placeholder='Start typing to find members'
                    value={recipientInput}
                    onChange={handleRecipientInput}
                    className='border-0 shadow-none flex-grow-1'
                  />
                </div>
                {filteredRecipients.length > 0 && (
                  <div
                    className='position-absolute w-100 bg-white border rounded mt-1'
                    style={{ zIndex: 1000 }}
                  >
                    {filteredRecipients.map((user) => (
                      <div
                        key={user.user_id}
                        className='p-2 hover-bg-light cursor-pointer'
                        onClick={() => addRecipient(user)}
                      >
                        {user.first_name} {user.last_name}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Message Display */}
          <div
            className='flex-grow-1 p-3'
            style={{
              backgroundColor: '#f1f5f9',
              overflowY: 'auto',
              height: '60vh',
            }}
          >
            {selectedChat ? (
              messages
                .filter(
                  (message) =>
                    !message.is_deleted &&
                    (message.sender === selectedChat ||
                      message.receiver === selectedChat)
                )
                .map((message, index) => {
                  const isSender =
                    user &&
                    message.sender === `${user.first_name} ${user.last_name}`

                  return (
                    <div
                      key={message.id}
                      className={`d-flex mb-3 ${
                        isSender
                          ? 'justify-content-end'
                          : 'justify-content-start'
                      }`}
                    >
                      {!isSender && (
                        <img
                          src={message.avatar || 'default-avatar.png'}
                          alt='avatar'
                          className='rounded-circle me-2'
                          style={{ width: '32px', height: '32px' }}
                        />
                      )}

                      <div
                        className={`p-3 rounded position-relative shadow-sm ${
                          isSender ? 'bg-indigo-600 ' : 'bg-gray-800 text-white'
                        }`}
                        style={{
                          maxWidth: '60%',
                          borderRadius: '12px',
                          color: isSender ? 'black' : 'white',
                        }}
                      >
                        <div className='d-flex justify-content-between align-items-center'>
                          {!isSender && (
                            <strong style={{ color: 'black' }}>
                              {message.sender}
                            </strong>
                          )}
                          <Dropdown align='end'>
                            <Dropdown.Toggle
                              as='button'
                              className='p-0 border-0 bg-transparent d-flex align-items-center'
                              style={{ all: 'unset', cursor: 'pointer' }}
                            >
                              <MoreHorizontal size={18} color='#ccc' />
                            </Dropdown.Toggle>
                            <Dropdown.Menu>
                              <Dropdown.Item
                                onClick={() =>
                                  setMessages(
                                    messages.map((m) =>
                                      m.id === message.id
                                        ? { ...m, is_deleted: true }
                                        : m
                                    )
                                  )
                                }
                              >
                                Delete
                              </Dropdown.Item>
                              <Dropdown.Item
                                onClick={() => setMessageInput(message.content)}
                              >
                                Forward
                              </Dropdown.Item>
                              <Dropdown.Item
                                onClick={() =>
                                  navigator.share({
                                    title: 'Message',
                                    text: message.content,
                                  })
                                }
                              >
                                Share
                              </Dropdown.Item>
                            </Dropdown.Menu>
                          </Dropdown>
                        </div>
                        <div className='text-black'>{message.content}</div>
                        {message.attachment && (
                          <div className='mt-2'>
                            <a
                              href={message.attachment}
                              download
                              className='text-decoration-none text-white'
                            >
                              📎 Attachment
                            </a>
                          </div>
                        )}
                        <small
                          className='d-block text-end mt-1 text-gray-400'
                          style={{ fontSize: '0.75rem', color: 'black' }}
                        >
                          {moment(message.timestamp).format('dddd HH:mm a')}
                        </small>
                      </div>
                    </div>
                  )
                })
            ) : (
              <p className='text-muted text-center'>
                Select a chat to start messaging
              </p>
            )}
          </div>

          {/* Message Input */}
          <div className='border-top p-3 bg-white'>
            <div className='d-flex align-items-center gap-2'>
              <Button
                variant='link'
                className='p-0 text-dark emoji-button'
                onClick={toggleEmojiPicker}
              >
                <Smile />
              </Button>
              {showEmojiPicker && (
                <div
                  className='emoji-picker-container'
                  style={{ position: 'absolute', bottom: '60px', zIndex: 1000 }}
                >
                  <EmojiPicker onEmojiClick={addEmoji} />
                </div>
              )}

              <Button
                variant='link'
                className='p-0 text-dark'
                onClick={toggleAttachments}
              >
                <Paperclip />
              </Button>
              {showAttachments && (
                <Form.Control
                  type='file'
                  onChange={handleFileInput}
                  className='d-none'
                  id='fileInput'
                />
              )}
              <Form.Control
                as='textarea'
                rows={1}
                placeholder='Type your message...'
                value={messageInput}
                onChange={(e) => setMessageInput(e.target.value)}
                onKeyPress={handleKeyPress}
                className='flex-grow-1 border-0 shadow-none'
              />
              <Button variant='primary' onClick={handleSendMessage}>
                <Send />
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Attachments Modal */}
      <Modal show={showAttachments} onHide={toggleAttachments} centered>
        <Modal.Header closeButton>
          <Modal.Title>Attach File</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group>
            <Form.Control type='file' onChange={handleFileInput} />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant='secondary' onClick={toggleAttachments}>
            Cancel
          </Button>
          <Button
            variant='primary'
            onClick={() => {
              if (fileInput) {
                handleSendMessage()
                toggleAttachments()
              }
            }}
          >
            Attach
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  )
}
